import { plural, t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { DocumentItem } from '@/modules/documents/components/document-item/DocumentItem'
import { MessageItem } from '@/modules/messages/components/message-item/MessageItem'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { useRelatedDocuments, useRelatedMessages, useRelatedTasks } from '@/modules/shared/hooks/useReleated'
import { TaskItem } from '@/modules/task/components/task-item/TaskItem'
import { openUrl } from '@/utils/external-resources'
import { RelatedItemsWrapper } from './Related.styled'

/**
 * Related items component (tasks and messages)
 * @param trigger - The trigger to get related items for
 * @returns The related items component
 */
export const RelatedItems = ({ trigger }: { trigger?: string }) => {
  const navigate = useNavigate()
  const { data: tasksResponse, isLoading: tasksIsLoading } = useRelatedTasks(trigger)
  const { data: messagesResponse, isLoading: messagesIsLoading } = useRelatedMessages(trigger)
  const { data: documentsResponse, isLoading: documentsIsLoading } = useRelatedDocuments(trigger)

  if (tasksIsLoading || messagesIsLoading || documentsIsLoading) return <AnimatedLoader small />

  if (tasksResponse?.data?.length === 0 && messagesResponse?.data?.length === 0 && documentsResponse?.length === 0) {
    return null
  }

  return (
    <StRelatedItemsSection>
      {/* Related tasks */}
      {tasksResponse?.data && tasksResponse?.data?.length > 0 ? (
        <RelatedItemsWrapper>
          <H3 noMargin>
            {t({
              id: 'related-tasks.title',
              message: plural(tasksResponse?.data?.length, {
                one: 'Bijhorende taak',
                other: 'Bijhorende taken',
              }),
            })}
          </H3>
          <StRelatedItemsList>
            {tasksResponse?.data?.map((task) => (
              <StRelatedItem
                key={task.id}
                onClick={() => {
                  mixpanel.track('timeline_related_task_open')
                  navigate(`/task/${task.id}`, {
                    state: {
                      from: 'timeline',
                    },
                  })
                }}
              >
                <TaskItem task={task} />
              </StRelatedItem>
            ))}
          </StRelatedItemsList>
        </RelatedItemsWrapper>
      ) : null}

      {/* Related messages */}
      {messagesResponse?.data && messagesResponse?.data?.length > 0 ? (
        <RelatedItemsWrapper>
          <H3 noMargin>
            {t({
              id: 'related-messages.title',
              message: plural(messagesResponse?.data?.length, {
                one: 'Bijhorend bericht',
                other: 'Bijhorende berichten',
              }),
            })}
          </H3>
          <StRelatedItemsList>
            {messagesResponse?.data?.map((message) => (
              <StRelatedItem
                key={message.id}
                onClick={() => {
                  mixpanel.track('timeline_related_message_open')
                  navigate(`/message/${message.id}`, {
                    state: {
                      from: 'timeline',
                    },
                  })
                }}
              >
                <MessageItem message={message} />
              </StRelatedItem>
            ))}
          </StRelatedItemsList>
        </RelatedItemsWrapper>
      ) : null}

      {/* Related documents */}
      {documentsResponse && documentsResponse?.length > 0 ? (
        <RelatedItemsWrapper>
          <H3 noMargin>
            {t({
              id: 'related-documents.title',
              message: plural(documentsResponse?.length, {
                one: 'Bijhorend document',
                other: 'Bijhorende documenten',
              }),
            })}
          </H3>

          <StRelatedItemsList>
            {documentsResponse?.map((document) => (
              <StRelatedItem
                key={document.id}
                onClick={() => {
                  mixpanel.track(`timeline_related_${document.meta?.messageType?.toLowerCase() ?? 'document'}_open`)

                  if (document.meta.attachments?.[0]?.url) {
                    openUrl(document.meta.attachments[0].url)
                  } else {
                    navigate(`/documents/personal/${document.id}`, {
                      state: {
                        from: 'timeline',
                      },
                    })
                  }
                }}
              >
                <DocumentItem document={document} />
              </StRelatedItem>
            ))}
          </StRelatedItemsList>
        </RelatedItemsWrapper>
      ) : null}
    </StRelatedItemsSection>
  )
}

const StRelatedItemsSection = styled(FlexColumn)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

const StRelatedItemsList = styled(FlexColumn)`
  gap: 0;
`

const StRelatedItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4} 0;

  cursor: pointer;

  border-bottom: 1px solid ${({ theme }) => theme.Colors.Neutrals.Light};

  &:last-child {
    border-bottom: none;
  }
`
